import { useCallback, useEffect, useMemo, useState } from "react";
import CaseSearch from '../../components/CaseSearch'
import CasesTable from '../../components/CasesTable'
import { 
    getCases 
} from '../../apis/cases'
import MainLayout from '../../containers/MainLayout'
import { hasPermissions } from "../../utils";
import { useCommonState } from "../../stores/common";
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";

const CasesList = () => {
    
    const allowedPerms = [ 'View' ]

    const commonStore = useCommonState()

    const [casesList, setCasesList] = useState([])
    const [totalPages, setTotalPages] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [keyword, setKeyword] = useState('')
    const [loading, setLoading] = useState(false)
    const [perms, setPerms] = useState(false)
    const [ministryFilter, setMinistryFilter] = useState(null)
    const [startDateFilter, setStartDateFilter] = useState(null)
    const [endDateFilter, setEndDateFilter] = useState(null)
    const [customNoResultsMessage, setCustomNoResultsMessage] = useState(null)
    const navigate = useNavigate()

    useEffect(() => {
        setPerms( commonStore.permissions )
    }, [commonStore.permissions])

    useEffect(() => {
        
        if (commonStore?.currentRole === 'Viewer') {
            navigate('/dashboard/search')
        }

    }, [commonStore?.currentRole])

    useEffect(() => {
        if ( hasPermissions(perms, ['View'])) {
            _getCasesList()
        }
    }, [commonStore.refreshCases])

    useEffect(() =>{
        _getCasesList()
    }, [ministryFilter, startDateFilter, endDateFilter, commonStore.currentRole])

    useEffect(() => {
        setCasesList([])
        if ( hasPermissions(perms, ['View'])) {
            _getCasesList()
        }
    }, [ keyword, currentPage, perms ])

    const _getCasesList = async () => {
        
        if ( loading || !commonStore?.currentRole ) {
            return
        }
        
        setLoading( true )
        console.log({
            page: currentPage,
            keyword: keyword,
            role: commonStore.currentRole,
            ministryFilter: ministryFilter,
            startDateFilter: startDateFilter,
            endDateFilter: endDateFilter
        })
        const result = await getCases({
            page: currentPage,
            keyword: keyword,
            role: commonStore.currentRole,
            ministryFilter: ministryFilter,
            startDateFilter: startDateFilter,
            endDateFilter: endDateFilter
        })
        setLoading( false )
        
        if ( result.success ) {
            const { data, totalPage, totalCount, resultMatching } = result.data

            // Handle Requestor role resultMatching logic
            if (commonStore.currentRole === 'Requestor' && keyword && typeof resultMatching !== 'undefined') {
                if (resultMatching === 1) {
                    // Show data as current logic
                    setCasesList( data )
                    setTotalPages( totalPage )
                    setTotalCount( totalCount )
                    setCustomNoResultsMessage( null )
                } else if (resultMatching === 2) {
                    // Cases found AND no cases submitted by requestor - show alert and custom message in table
                    const message = `A record is found for ${keyword}`;

                    Swal.fire({
                        icon: 'warning',
                        title: '',
                        html: `<p>${message}</p>`,
                        confirmButtonText: 'OK'
                    });

                    // Show data (no need to force empty array) and set custom message for table
                    setCasesList( data )
                    setTotalPages( totalPage )
                    setTotalCount( totalCount )
                    setCustomNoResultsMessage( message )
                } else if (resultMatching === 0) {
                    // No records found - no alert, just show empty data
                    setCasesList( data )
                    setTotalPages( totalPage )
                    setTotalCount( totalCount )
                    setCustomNoResultsMessage( null )
                }
            }
            // Handle Approver role resultMatching logic
            else if (commonStore.currentRole === 'Approver' && keyword && typeof resultMatching !== 'undefined') {
                if (resultMatching === 1) {
                    // Show data as current logic
                    setCasesList( data )
                    setTotalPages( totalPage )
                    setTotalCount( totalCount )
                    setCustomNoResultsMessage( null )
                } else if (resultMatching === 2) {
                    // Cases found AND no cases assigned to approver - show alert and custom message in table
                    const message = `A record is found for ${keyword}`;

                    Swal.fire({
                        icon: 'warning',
                        title: '',
                        html: `<p>${message}</p>`,
                        confirmButtonText: 'OK'
                    });

                    // Show data (no need to force empty array) and set custom message for table
                    setCasesList( data )
                    setTotalPages( totalPage )
                    setTotalCount( totalCount )
                    setCustomNoResultsMessage( message )
                } else if (resultMatching === 0) {
                    // No records found - no alert, just show empty data
                    setCasesList( data )
                    setTotalPages( totalPage )
                    setTotalCount( totalCount )
                    setCustomNoResultsMessage( null )
                }
            } else {
                // Normal behavior for non-Approver/non-Requestor or non-search scenarios
                setCasesList( data )
                setTotalPages( totalPage )
                setTotalCount( totalCount )
                setCustomNoResultsMessage( null )
            }
        }
    }
    
    return (
        <MainLayout allowedPerms={allowedPerms}>
            
            <div className="content-wrapper">
                <h1 className="mt-5 mb-5 font-weight-bold">Cases List</h1>
            
                <CaseSearch 
                    onKeywordChange={(value) => setKeyword(value)}
                    onMinistryFilterChange={(data) => setMinistryFilter(data)}
                    onStartDateFilterChange={(data) => setStartDateFilter(data)}
                    onEndDateFilterChange={(data) => setEndDateFilter(data)}
                />

                <CasesTable 
                    onPageChanged={(nextPage) => setCurrentPage( nextPage )}
                    cases={casesList}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    currentPage={currentPage}
                    loading={loading}
                    customNoResultsMessage={customNoResultsMessage}
                />
            </div>
            
        </MainLayout>
    )
}

export default CasesList